import React, { useState } from "react";
import { Clock, Calendar, PieChart, Search, X } from "lucide-react";
import { Input } from "@/components/ui";
import { DigestSubscription, DigestFrequency } from "@/services/bedrock";
import { DigestSubscriptionDialog } from "./components/DigestSubscriptionDialog";
import { DigestHistoryDialog } from "./components/DigestHistoryDialog";
import { SubscriptionSection } from "./components/SubscriptionSection";
import { useDebounced } from "@/utils";

export const DigestEmails: React.FC = () => {
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearch = useDebounced(searchValue, 300);

  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    frequency: DigestFrequency;
    subscription: DigestSubscription | null;
  }>({
    isOpen: false,
    frequency: "weekly",
    subscription: null,
  });

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState<DigestSubscription | null>(null);

  const handleEdit = (subscription: DigestSubscription) => {
    setDialogState({
      isOpen: true,
      frequency: subscription.frequency,
      subscription: subscription,
    });
  };

  const handleCreate = (frequency: DigestFrequency) => {
    setDialogState({
      isOpen: true,
      frequency,
      subscription: null,
    });
  };

  const handleHistory = (subscription: DigestSubscription) => {
    setSelectedSubscription(subscription);
    setIsHistoryOpen(true);
  };

  return (
    <div className="flex flex-col gap-12 pb-20">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Search subscriptions..."
          className="pl-9 pr-10 h-10 bg-white rounded-xl border-slate-200 shadow-sm focus:ring-primary/20 transition-all"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
        />
        {searchValue && (
          <button
            type="button"
            onClick={() => setSearchValue("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 size-5 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400 transition-colors"
          >
            <X className="size-3" />
          </button>
        )}
      </div>

      {/* DAILY SECTION */}
      <SubscriptionSection
        title="Daily Digests"
        description="Tactical action. Immediate follow-ups on the last 24h of activities and urgent tasks."
        icon={<Clock className="size-5 text-blue-600" />}
        frequency="daily"
        search={debouncedSearch}
        onEdit={handleEdit}
        onHistory={handleHistory}
        onCreate={() => handleCreate("daily")}
      />

      {/* WEEKLY SECTION */}
      <SubscriptionSection
        title="Weekly Recaps"
        description="Performance and trends. See how your team performed over the last 7 days compared to the previous week."
        icon={<PieChart className="size-5 text-emerald-600" />}
        frequency="weekly"
        search={debouncedSearch}
        onEdit={handleEdit}
        onHistory={handleHistory}
        onCreate={() => handleCreate("weekly")}
      />

      {/* MONTHLY SECTION */}
      <SubscriptionSection
        title="Monthly Reports"
        description="Strategic review. High-level snapshots for long-term planning, identifying top performers, and spotting seasonal shifts."
        icon={<Calendar className="size-5 text-purple-600" />}
        frequency="monthly"
        search={debouncedSearch}
        onEdit={handleEdit}
        onHistory={handleHistory}
        onCreate={() => handleCreate("monthly")}
      />

      {dialogState.isOpen && (
        <DigestSubscriptionDialog
          open={dialogState.isOpen}
          onOpenChange={open => setDialogState(prev => ({ ...prev, isOpen: open }))}
          subscription={dialogState.subscription}
          fixedFrequency={dialogState.frequency}
        />
      )}

      {isHistoryOpen && selectedSubscription && (
        <DigestHistoryDialog open={isHistoryOpen} onOpenChange={setIsHistoryOpen} subscription={selectedSubscription} />
      )}
    </div>
  );
};
